import { useCallback, useEffect, useMemo, useState } from 'react';
import { useListenerLatch } from './useListenerLatch';
import {
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  setDoc,
  Timestamp,
  updateDoc,
  writeBatch,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  binderPlacementCountDoc,
  binderPlacementCountsCollection,
  binderPlacementDoc,
  binderPlacementsCollection,
  userBinderDoc,
  userBindersCollection,
  userCollectionBagDoc,
  userDoc,
} from '../firebase/collections';
import type {
  BinderAppearance,
  BinderLayout,
  BinderPlacement,
  BinderPlacementDraft,
  RiftboundCard,
  UserBinder,
} from '../types';
import { useAuth } from './useAuth';
import {
  countCustomBinders,
  customBinderLimitReachedMessage,
  isAtCustomBinderLimit,
  USER_LIMITS,
} from '../constants/userLimits';
import {
  CUSTOM_BINDER_DEFAULT_PAGE_LIMIT,
  DEFAULT_BINDER_APPEARANCE,
  DEFAULT_BINDER_LAYOUT,
  resolveBinderLayout,
  SET_BINDER_DEFAULT_PAGE_LIMIT,
  setBinderLayoutForCatalog,
  setBinderLayoutForSetCode,
  validateBinderPlacement,
  isBinderDeletable,
} from '../utils/binderUtils';
import { ownedQuantitiesFromSnaps } from '../utils/collectionBag';
import { commitBatchChunks, deleteRefsInBatches } from '../utils/firestoreBatch';

type CreateBinderInput = {
  name: string;
  kind: UserBinder['kind'];
  setCode?: string;
  setName?: string;
  layout?: BinderLayout;
  appearance?: Partial<BinderAppearance>;
  showMissingCards?: boolean;
  placements?: BinderPlacementDraft[];
  modelId?: string;
  pageLimit?: number;
  sortIndex?: number;
};

type CreateSetBinderInput = {
  name?: string;
  setCode: string;
  setName?: string;
  /** Full set catalog — used to pick the smallest Zipfolio layout that fits. */
  catalogCards?: RiftboundCard[];
  layout?: BinderLayout;
  appearance?: Partial<BinderAppearance>;
  showMissingCards?: boolean;
  modelId?: string;
  pageLimit?: number;
  sortIndex?: number;
};

function normalizeSetCode(setCode: string | undefined): string {
  return (setCode || '').trim().toUpperCase();
}

function setBinderId(setCode: string): string {
  return `set-${setCode.toLowerCase()}`;
}

function nextCustomBinderId(binders: UserBinder[]): string {
  const used = new Set(binders.filter((binder) => binder.kind === 'custom').map((binder) => binder.id));
  for (let index = 0; index < USER_LIMITS.MAX_CUSTOM_BINDERS_PER_USER; index += 1) {
    const id = `custom-${index}`;
    if (!used.has(id)) return id;
  }
  throw new Error(customBinderLimitReachedMessage());
}

function millis(value: unknown): number {
  return (value as Timestamp | undefined)?.toMillis?.() || Date.now();
}

function binderFromFirestore(id: string, data: DocumentData): UserBinder {
  const appearance = data.appearance as Partial<BinderAppearance> | undefined;
  const binder: UserBinder = {
    id,
    ownerId: (data.ownerId as string) || '',
    name: (data.name as string) || 'Untitled Binder',
    kind: (data.kind as UserBinder['kind']) || 'custom',
    setCode: (data.setCode as string | undefined) || undefined,
    setName: (data.setName as string | undefined) || undefined,
    layout: (data.layout as BinderLayout) || DEFAULT_BINDER_LAYOUT,
    sortIndex: (data.sortIndex as number) ?? 0,
    appearance: {
      ...DEFAULT_BINDER_APPEARANCE,
      ...(appearance || {}),
    },
    showMissingCards: Boolean(data.showMissingCards),
    modelId: (data.modelId as string | undefined) || undefined,
    pageLimit: typeof data.pageLimit === 'number' ? (data.pageLimit as number) : undefined,
    createdAt: millis(data.createdAt),
    updatedAt: millis(data.updatedAt),
  };
  return { ...binder, layout: resolveBinderLayout(binder) };
}

function placementFromFirestore(slotKey: string, data: DocumentData): BinderPlacement {
  return {
    slotKey,
    cardId: (data.cardId as string) || '',
    pageIndex: (data.pageIndex as number) ?? 0,
    slotIndex: (data.slotIndex as number) ?? 0,
    foil: Boolean(data.foil),
    createdAt: millis(data.createdAt),
    updatedAt: millis(data.updatedAt),
  };
}

function placementCountsFromFirestore(data: DocumentData | undefined): { total: number; foil: number } {
  return {
    total: Math.max(0, Number(data?.total) || 0),
    foil: Math.max(0, Number(data?.foil) || 0),
  };
}

function countPlacementDrafts(
  placements: BinderPlacementDraft[],
): Map<string, { total: number; foil: number }> {
  const counts = new Map<string, { total: number; foil: number }>();
  for (const placement of placements) {
    if (!placement.cardId) continue;
    const current = counts.get(placement.cardId) ?? { total: 0, foil: 0 };
    current.total += 1;
    if (placement.foil) current.foil += 1;
    counts.set(placement.cardId, current);
  }
  return counts;
}

function serializeAppearance(appearance?: Partial<BinderAppearance>): BinderAppearance {
  return {
    ...DEFAULT_BINDER_APPEARANCE,
    ...(appearance || {}),
  };
}

function buildBinderPayload(
  userId: string,
  input: CreateBinderInput,
  normalizedSetCode: string,
  sortIndex: number,
  now: Timestamp,
): Record<string, unknown> {
  const pageLimit = input.pageLimit ?? (input.kind === 'custom' && !input.modelId
    ? CUSTOM_BINDER_DEFAULT_PAGE_LIMIT
    : undefined);
  return {
    ownerId: userId,
    name: input.name.trim() || 'Untitled Binder',
    kind: input.kind,
    setCode: normalizedSetCode,
    setName: input.setName || '',
    layout: input.layout || (input.kind === 'set' && input.setCode
      ? setBinderLayoutForSetCode(input.setCode)
      : DEFAULT_BINDER_LAYOUT),
    sortIndex,
    appearance: serializeAppearance(input.appearance),
    showMissingCards: Boolean(input.showMissingCards),
    ...(input.modelId ? { modelId: input.modelId } : {}),
    ...(pageLimit != null ? { pageLimit } : {}),
    createdAt: now,
    updatedAt: now,
  };
}

async function writePlacementBatches(
  uid: string,
  binderId: string,
  placements: BinderPlacementDraft[],
  now: Timestamp,
): Promise<void> {
  if (placements.length === 0) return;
  await commitBatchChunks(db, placements, (batch, chunk) => {
    for (const placement of chunk) {
      const slotKey = `p${placement.pageIndex}-s${placement.slotIndex}`;
      batch.set(binderPlacementDoc(uid, binderId, slotKey), {
        cardId: placement.cardId,
        pageIndex: placement.pageIndex,
        slotIndex: placement.slotIndex,
        foil: Boolean(placement.foil),
        createdAt: now,
        updatedAt: now,
      });
    }
  });
  await commitBatchChunks(db, [...countPlacementDrafts(placements)], (batch, chunk) => {
    for (const [cardId, count] of chunk) {
      batch.set(binderPlacementCountDoc(uid, binderId, cardId), {
        cardId,
        total: count.total,
        foil: count.foil,
        updatedAt: now,
      });
    }
  });
}

export function useBinders(options?: { enabled?: boolean }): {
  binders: UserBinder[];
  loading: boolean;
  error: string | null;
  createBinder: (input: CreateBinderInput) => Promise<string>;
  createSetBinder: (input: CreateSetBinderInput) => Promise<string>;
  updateBinder: (binderId: string, patch: Partial<Omit<UserBinder, 'id' | 'ownerId' | 'createdAt' | 'updatedAt'>>) => Promise<void>;
  deleteBinder: (binderId: string) => Promise<void>;
  reorderBinders: (binderIds: string[]) => Promise<void>;
} {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const enabled = options?.enabled !== false;
  const keepAlive = useListenerLatch(uid, enabled);
  const [binders, setBinders] = useState<UserBinder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) {
      setBinders([]);
      setLoading(false);
      return;
    }
    if (!keepAlive) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(userBindersCollection(uid), orderBy('sortIndex', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const result = snap.docs.map((d) => binderFromFirestore(d.id, d.data()));
        result.sort((a, b) => a.sortIndex - b.sortIndex || b.updatedAt - a.updatedAt);
        setBinders(result);
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.error('[useBinders] snapshot error', err);
        setError(err.message);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [uid, keepAlive]);

  useEffect(() => {
    if (!user || loading || !keepAlive) return;
    if (user.binderCount === binders.length) return;
    void setDoc(userDoc(user.uid), { binderCount: binders.length }, { merge: true }).catch((err) => {
      console.warn('[useBinders] binderCount sync failed (non-fatal)', err);
    });
  }, [user, loading, keepAlive, binders.length]);

  const createBinder = useCallback(
    async (input: CreateBinderInput): Promise<string> => {
      if (!user) throw new Error('Must be signed in to create a binder.');
      const normalizedSetCode = normalizeSetCode(input.setCode);
      if (input.kind === 'set') {
        if (!normalizedSetCode) throw new Error('Choose a set for this binder.');
        const existing = binders.find(
          (binder) => binder.kind === 'set' && normalizeSetCode(binder.setCode) === normalizedSetCode,
        );
        if (existing) return existing.id;
      } else if (isAtCustomBinderLimit(countCustomBinders(binders))) {
        throw new Error(customBinderLimitReachedMessage());
      }

      const now = Timestamp.now();
      const sortIndex = input.sortIndex ?? (binders.length
        ? Math.max(...binders.map((binder) => binder.sortIndex)) + 1
        : 0);
      const binderPayload = buildBinderPayload(user.uid, input, normalizedSetCode, sortIndex, now);

      if (input.kind === 'set' && normalizedSetCode) {
        const ref = userBinderDoc(user.uid, setBinderId(normalizedSetCode));
        const created = await runTransaction(db, async (tx) => {
          const existingSnap = await tx.get(ref);
          if (existingSnap.exists()) return false;
          tx.set(ref, binderPayload);
          return true;
        });
        if (!created) return ref.id;
        return ref.id;
      }

      const ref = userBinderDoc(user.uid, nextCustomBinderId(binders));
      await setDoc(ref, binderPayload);
      await writePlacementBatches(user.uid, ref.id, input.placements || [], now);
      return ref.id;
    },
    [user, binders],
  );

  const createSetBinder = useCallback(
    async (input: CreateSetBinderInput): Promise<string> => {
      const catalog = input.catalogCards ?? [];
      const layout = input.layout
        || (catalog.length ? setBinderLayoutForCatalog(catalog) : setBinderLayoutForSetCode(input.setCode));
      const pageLimit = input.pageLimit ?? (input.modelId ? undefined : SET_BINDER_DEFAULT_PAGE_LIMIT);
      return createBinder({
        name: input.name?.trim() || input.setName || input.setCode,
        kind: 'set',
        setCode: input.setCode,
        setName: input.setName,
        layout,
        appearance: {
          coverMode: 'setArt',
          ...input.appearance,
        },
        showMissingCards: input.showMissingCards ?? true,
        modelId: input.modelId,
        pageLimit,
        sortIndex: input.sortIndex,
      });
    },
    [createBinder],
  );

  const updateBinder = useCallback(
    async (
      binderId: string,
      patch: Partial<Omit<UserBinder, 'id' | 'ownerId' | 'createdAt' | 'updatedAt'>>,
    ) => {
      if (!user) throw new Error('Must be signed in.');
      await updateDoc(userBinderDoc(user.uid, binderId), {
        ...patch,
        updatedAt: Timestamp.now(),
      });
    },
    [user],
  );

  const deleteBinder = useCallback(
    async (binderId: string) => {
      if (!user) throw new Error('Must be signed in.');
      const binder = binders.find((item) => item.id === binderId);
      if (binder && !isBinderDeletable(binder)) {
        throw new Error('This set binder is part of your default shelf and cannot be deleted.');
      }
      const placements = await getDocs(binderPlacementsCollection(user.uid, binderId));
      const counts = await getDocs(binderPlacementCountsCollection(user.uid, binderId));
      await deleteRefsInBatches(
        db,
        [
          ...placements.docs.map((placement) => placement.ref),
          ...counts.docs.map((count) => count.ref),
        ],
        userBinderDoc(user.uid, binderId),
      );
    },
    [user, binders],
  );

  const reorderBinders = useCallback(
    async (binderIds: string[]) => {
      if (!user) throw new Error('Must be signed in.');
      const currentSortById = new Map(binders.map((binder) => [binder.id, binder.sortIndex]));
      const changedIds = binderIds.filter((binderId, sortIndex) => currentSortById.get(binderId) !== sortIndex);
      if (changedIds.length === 0) return;

      const batch = writeBatch(db);
      const now = Timestamp.now();
      changedIds.forEach((binderId) => {
        const sortIndex = binderIds.indexOf(binderId);
        batch.update(userBinderDoc(user.uid, binderId), { sortIndex, updatedAt: now });
      });
      await batch.commit();
    },
    [user, binders],
  );

  return {
    binders,
    loading,
    error,
    createBinder,
    createSetBinder,
    updateBinder,
    deleteBinder,
    reorderBinders,
  };
}

export function useBinder(binderId: string | undefined): {
  binder: UserBinder | null;
  placements: BinderPlacement[];
  placementsBySlot: Record<string, BinderPlacement>;
  loading: boolean;
  error: string | null;
  placeCard: (placement: BinderPlacementDraft, options?: { sourceSlotKey?: string }) => Promise<void>;
  swapPlacements: (slotKeyA: string, slotKeyB: string) => Promise<void>;
  removePlacement: (slotKey: string) => Promise<void>;
  updateBinder: (patch: Partial<Omit<UserBinder, 'id' | 'ownerId' | 'createdAt' | 'updatedAt'>>) => Promise<void>;
} {
  const { user } = useAuth();
  const [binder, setBinder] = useState<UserBinder | null>(null);
  const [placements, setPlacements] = useState<BinderPlacement[]>([]);
  const [binderLoading, setBinderLoading] = useState(Boolean(binderId));
  const [placementsLoading, setPlacementsLoading] = useState(Boolean(binderId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !binderId) {
      setBinder(null);
      setBinderLoading(false);
      return;
    }
    setBinderLoading(true);
    const unsub = onSnapshot(
      userBinderDoc(user.uid, binderId),
      (snap) => {
        setBinder(snap.exists() ? binderFromFirestore(snap.id, snap.data()) : null);
        setError(snap.exists() ? null : 'Binder not found.');
        setBinderLoading(false);
      },
      (err) => {
        console.error('[useBinder] binder snapshot error', err);
        setError(err.message);
        setBinderLoading(false);
      },
    );
    return () => unsub();
  }, [user, binderId]);

  const activeBinderId = binder?.id;
  const activeBinderKind = binder?.kind;

  useEffect(() => {
    if (!user || !binderId || activeBinderId !== binderId || activeBinderKind !== 'custom') {
      setPlacements([]);
      setPlacementsLoading(false);
      return;
    }
    setPlacementsLoading(true);
    const q = query(
      binderPlacementsCollection(user.uid, binderId),
      orderBy('pageIndex', 'asc'),
      orderBy('slotIndex', 'asc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setPlacements(
          snap.docs
            .map((d) => placementFromFirestore(d.id, d.data()))
            .filter((placement) => Boolean(placement.cardId)),
        );
        setPlacementsLoading(false);
      },
      (err) => {
        console.error('[useBinder] placements snapshot error', err);
        setError(err.message);
        setPlacementsLoading(false);
      },
    );
    return () => unsub();
  }, [user, binderId, activeBinderId, activeBinderKind]);

  const placementsBySlot = useMemo(() => {
    const result: Record<string, BinderPlacement> = {};
    for (const placement of placements) result[placement.slotKey] = placement;
    return result;
  }, [placements]);

  const placeCard = useCallback(
    async (placement: BinderPlacementDraft, options?: { sourceSlotKey?: string }) => {
      if (!user || !binderId) throw new Error('Must be signed in.');
      if (!placement.cardId?.trim()) throw new Error('Invalid card.');
      if (
        !Number.isInteger(placement.pageIndex)
        || !Number.isInteger(placement.slotIndex)
        || placement.pageIndex < 0
        || placement.slotIndex < 0
      ) {
        throw new Error('Drop on an empty pocket to place a card.');
      }

      const targetSlotKey = `p${placement.pageIndex}-s${placement.slotIndex}`;
      if (options?.sourceSlotKey === targetSlotKey) return;

      const now = Timestamp.now();
      await runTransaction(db, async (tx) => {
        const binderRef = userBinderDoc(user.uid, binderId);
        const bagRef = userCollectionBagDoc(user.uid);
        const countRef = binderPlacementCountDoc(user.uid, binderId, placement.cardId);
        const targetRef = binderPlacementDoc(user.uid, binderId, targetSlotKey);
        const sourceRef = options?.sourceSlotKey
          ? binderPlacementDoc(user.uid, binderId, options.sourceSlotKey)
          : null;

        const [binderSnap, bagSnap, countSnap, targetSnap, sourceSnap] = await Promise.all([
          tx.get(binderRef),
          tx.get(bagRef),
          tx.get(countRef),
          tx.get(targetRef),
          sourceRef ? tx.get(sourceRef) : Promise.resolve(null),
        ]);

        if (!binderSnap.exists()) throw new Error('Binder not found.');
        if (binderSnap.data().kind !== 'custom') {
          throw new Error('Set binders use fixed collector-number positions.');
        }

        const { quantity: entryQuantity, quantityFoil: entryQuantityFoil } =
          ownedQuantitiesFromSnaps(bagSnap.data(), placement.cardId);

        const existingTarget = targetSnap.exists()
          ? placementFromFirestore(targetSnap.id, targetSnap.data() as DocumentData)
          : null;
        const sourcePlacement = sourceSnap?.exists()
          ? placementFromFirestore(sourceSnap.id, sourceSnap.data() as DocumentData)
          : null;
        if (existingTarget && existingTarget.slotKey !== options?.sourceSlotKey) {
          throw new Error('Pocket already has a card.');
        }
        if (sourceRef && (!sourcePlacement || sourcePlacement.cardId !== placement.cardId)) {
          throw new Error('Source pocket no longer contains this card.');
        }

        const currentCount = placementCountsFromFirestore(countSnap.data());
        const removedTotal = sourcePlacement ? 1 : 0;
        const removedFoil = sourcePlacement?.foil ? 1 : 0;
        const nextTotal = currentCount.total - removedTotal + 1;
        const nextFoil = currentCount.foil - removedFoil + (placement.foil ? 1 : 0);
        const validation = validateBinderPlacement({
          placement,
          placements: Array.from({ length: Math.max(0, currentCount.total - removedTotal) }, (_, index) => ({
            slotKey: `count-${index}`,
            cardId: placement.cardId,
            pageIndex: 0,
            slotIndex: index,
            foil: index < Math.max(0, currentCount.foil - removedFoil),
            createdAt: 0,
            updatedAt: 0,
          })),
          entries: {
            [placement.cardId]: {
              cardId: placement.cardId,
              quantity: entryQuantity,
              quantityFoil: entryQuantityFoil,
              addedAt: 0,
              updatedAt: 0,
            },
          },
          pageLimit: typeof binderSnap.data().pageLimit === 'number'
            ? (binderSnap.data().pageLimit as number)
            : undefined,
        });
        if (!validation.valid) throw new Error(validation.reason);
        if (nextTotal < 0 || nextFoil < 0 || nextFoil > nextTotal) {
          throw new Error('Binder placement counts are out of sync.');
        }

        if (sourceRef) {
          tx.delete(sourceRef);
        }
        tx.set(
          targetRef,
          {
            cardId: placement.cardId,
            pageIndex: placement.pageIndex,
            slotIndex: placement.slotIndex,
            foil: Boolean(placement.foil),
            createdAt: existingTarget?.createdAt
              ? Timestamp.fromMillis(existingTarget.createdAt)
              : now,
            updatedAt: now,
          },
          { merge: true },
        );
        tx.set(countRef, {
          cardId: placement.cardId,
          total: nextTotal,
          foil: nextFoil,
          updatedAt: now,
        });
        tx.update(binderRef, { updatedAt: now });
      });
    },
    [user, binderId],
  );

  const swapPlacements = useCallback(
    async (slotKeyA: string, slotKeyB: string) => {
      if (!user || !binderId) throw new Error('Must be signed in.');
      if (slotKeyA === slotKeyB) return;

      const placementA = placementsBySlot[slotKeyA];
      const placementB = placementsBySlot[slotKeyB];
      if (!placementA || !placementB) {
        throw new Error('Both pockets must have a card to swap.');
      }

      const now = Timestamp.now();
      await runTransaction(db, async (tx) => {
        const binderRef = userBinderDoc(user.uid, binderId);
        const refA = binderPlacementDoc(user.uid, binderId, slotKeyA);
        const refB = binderPlacementDoc(user.uid, binderId, slotKeyB);
        const [binderSnap, snapA, snapB] = await Promise.all([
          tx.get(binderRef),
          tx.get(refA),
          tx.get(refB),
        ]);
        if (!binderSnap.exists()) throw new Error('Binder not found.');
        if (binderSnap.data().kind !== 'custom') {
          throw new Error('Set binders use fixed collector-number positions.');
        }
        if (!snapA.exists() || !snapB.exists()) {
          throw new Error('Both pockets must have a card to swap.');
        }

        tx.set(
          refA,
          {
            cardId: placementB.cardId,
            pageIndex: placementA.pageIndex,
            slotIndex: placementA.slotIndex,
            foil: placementB.foil,
            createdAt: placementA.createdAt
              ? Timestamp.fromMillis(placementA.createdAt)
              : now,
            updatedAt: now,
          },
          { merge: true },
        );
        tx.set(
          refB,
          {
            cardId: placementA.cardId,
            pageIndex: placementB.pageIndex,
            slotIndex: placementB.slotIndex,
            foil: placementA.foil,
            createdAt: placementB.createdAt
              ? Timestamp.fromMillis(placementB.createdAt)
              : now,
            updatedAt: now,
          },
          { merge: true },
        );
        tx.update(binderRef, { updatedAt: now });
      });
    },
    [user, binderId, placementsBySlot],
  );

  const removePlacement = useCallback(
    async (slotKey: string) => {
      if (!user || !binderId) throw new Error('Must be signed in.');
      const now = Timestamp.now();
      await runTransaction(db, async (tx) => {
        const binderRef = userBinderDoc(user.uid, binderId);
        const placementRef = binderPlacementDoc(user.uid, binderId, slotKey);
        const [binderSnap, placementSnap] = await Promise.all([
          tx.get(binderRef),
          tx.get(placementRef),
        ]);
        if (!binderSnap.exists()) throw new Error('Binder not found.');
        if (binderSnap.data().kind !== 'custom') {
          throw new Error('Set binders use fixed collector-number positions.');
        }
        if (!placementSnap.exists()) return;

        const placement = placementFromFirestore(placementSnap.id, placementSnap.data() as DocumentData);
        const countRef = binderPlacementCountDoc(user.uid, binderId, placement.cardId);
        const countSnap = await tx.get(countRef);
        const currentCount = placementCountsFromFirestore(countSnap.data());
        const nextTotal = Math.max(0, currentCount.total - 1);
        const nextFoil = Math.max(0, currentCount.foil - (placement.foil ? 1 : 0));

        tx.delete(placementRef);
        if (nextTotal > 0) {
          tx.set(countRef, {
            cardId: placement.cardId,
            total: nextTotal,
            foil: Math.min(nextFoil, nextTotal),
            updatedAt: now,
          });
        } else {
          tx.delete(countRef);
        }
        tx.update(binderRef, { updatedAt: now });
      });
    },
    [user, binderId],
  );

  const updateBinder = useCallback(
    async (patch: Partial<Omit<UserBinder, 'id' | 'ownerId' | 'createdAt' | 'updatedAt'>>) => {
      if (!user || !binderId) throw new Error('Must be signed in.');
      await updateDoc(userBinderDoc(user.uid, binderId), {
        ...patch,
        updatedAt: Timestamp.now(),
      });
    },
    [user, binderId],
  );

  return {
    binder,
    placements,
    placementsBySlot,
    loading: binderLoading || placementsLoading,
    error,
    placeCard,
    swapPlacements,
    removePlacement,
    updateBinder,
  };
}
